import { useState, useEffect } from 'react';
import { X, Bell, Check, Crown, CreditCard, AlertTriangle, Gift, Trophy, Shield } from 'lucide-react';

const NOTIFICATION_ICONS = {
  payment_success: CreditCard,
  withdrawal: CreditCard,
  challenge_won: Trophy,
  challenge_lost: Trophy,
  new_backer: Gift,
  backer_reward: Crown,
  referral_bonus: Gift,
  referral_reward: Gift,
  security: Shield,
  rank_change: Crown,
  verified: Shield,
  default: Bell
};

const NOTIFICATION_COLORS = {
  payment_success: 'border-green-500 bg-green-500/10',
  withdrawal: 'border-cyan-500 bg-cyan-500/10',
  challenge_won: 'border-lime bg-lime/10',
  challenge_lost: 'border-red-500 bg-red-500/10',
  new_backer: 'border-purple-500 bg-purple-500/10',
  backer_reward: 'border-lime bg-lime/10',
  referral_bonus: 'border-muted-gold bg-muted-gold/10',
  referral_reward: 'border-muted-gold bg-muted-gold/10',
  security: 'border-yellow-500 bg-yellow-500/10',
  rank_change: 'border-lime bg-lime/10',
  verified: 'border-green-500 bg-green-500/10',
  default: 'border-gray-600 bg-gray-600/10'
};

export default function NotificationPopup({ notification, onClose, onMarkRead }) {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    setTimeout(() => setIsVisible(true), 50);

    // Auto-dismiss after 8 seconds
    const autoCloseTimer = setTimeout(() => {
      handleClose();
    }, 8000);

    return () => clearTimeout(autoCloseTimer);
  }, []);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose(notification.id);
    }, 300);
  };

  const handleMarkRead = () => {
    onMarkRead(notification.id);
    handleClose();
  };

  const IconComponent = NOTIFICATION_ICONS[notification.type] || NOTIFICATION_ICONS.default;
  const colorClass = NOTIFICATION_COLORS[notification.type] || NOTIFICATION_COLORS.default;

  return (
    <div
      className={`
        fixed top-20 right-4 z-[100] max-w-sm w-full
        transform transition-all duration-300 ease-out
        ${isVisible && !isExiting ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
      data-testid="notification-popup"
    >
      <div className={`
        bg-gray-900/95 backdrop-blur-md rounded-xl border-l-4 shadow-2xl
        ${colorClass}
      `}>
        {/* Header */}
        <div className="flex items-start justify-between p-4 pb-2">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${colorClass}`}>
              <IconComponent className="w-5 h-5 text-white" />
            </div>
            <div>
              <h4 className="font-bold text-white text-sm">{notification.title}</h4>
              <p className="text-xs text-muted">Just now</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-muted hover:text-white transition-colors p-1"
            data-testid="notification-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="px-4 pb-3">
          <p className="text-sm text-gray-300">{notification.message}</p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between px-4 pb-4">
          <button
            onClick={handleMarkRead}
            className="flex items-center gap-2 text-xs text-muted-gold hover:text-cyan-300 transition-colors"
            data-testid="notification-mark-read"
          >
            <Check className="w-3 h-3" />
            Mark as read
          </button>
          <div className="h-1 w-full max-w-[100px] bg-gray-700 rounded-full overflow-hidden ml-4">
            <div 
              className="h-full bg-muted-gold rounded-full animate-shrink"
              style={{
                animation: 'shrink 8s linear forwards'
              }}
            />
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
}
